import { useState, useRef, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import FileUploader from "@/components/features/FileUploader";
import DomainPreview from "@/components/features/DomainPreview";
import SearchConfig from "@/components/features/SearchConfig";
import CheckoutButtons from "@/components/features/CheckoutButtons";
import PaymentThankYou from "@/components/features/PaymentThankYou";
import { useFileUpload } from "@/hooks/useFileUpload";
import { useCheckout } from "@/hooks/useCheckout";
import { useLanguage } from "@/lib/LanguageContext";
import api from "@/lib/api";

const HeroSection = ({ selectedTier, setSelectedTier }) => {
  const [searchParams] = useSearchParams();
  const [countryCode, setCountryCode] = useState("ALL");
  const [adFormat, setAdFormat] = useState("ALL");
  const [paymentStatus, setPaymentStatus] = useState(null);
  const uploadSectionRef = useRef(null);
  const { t } = useLanguage();

  const {
    file,
    domains,
    fileId,
    isUploading,
    isDragOver,
    uploadProgress,
    handleDragOver,
    handleDragLeave,
    handleDrop,
    handleFileSelect,
    resetUpload,
    removeFile,
  } = useFileUpload();

  const { isProcessing, handleCheckout: doCheckout } = useCheckout();

  useEffect(() => {
    const sessionId = searchParams.get("session_id");
    const status = searchParams.get("status");

    if (sessionId && status === "success") {
      setPaymentStatus("thankyou");

      // Poll backend to trigger processing (backup to webhook)
      const pollStatus = async () => {
        try {
          await api.get(`/payment/status/${sessionId}`);
        } catch (err) {
          console.warn("Could not poll payment status:", err);
        }
      };
      pollStatus();
    } else if (status === "cancelled") {
      toast.error(t("toast.paymentCancelled"));
    }
  }, [searchParams, t]);

  const handleReset = () => {
    resetUpload();
    setPaymentStatus(null);
    setSelectedTier(null);
    window.history.replaceState({}, document.title, window.location.pathname);
  };

  const onCheckout = (tier) => {
    doCheckout(tier, fileId, countryCode, adFormat);
  };

  return (
    <section ref={uploadSectionRef} className="max-w-7xl mx-auto px-6 py-16 md:py-24">
      {/* Payment Thank You Status */}
      <AnimatePresence>
        {paymentStatus && <PaymentThankYou onReset={handleReset} />}
      </AnimatePresence>

      {!paymentStatus && (
        <>
          {/* Hero Text */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h1
              className="text-3xl sm:text-4xl lg:text-6xl font-black text-white mb-6 leading-[1.15] max-w-4xl mx-auto text-balance"
              style={{ fontFamily: 'Inter, sans-serif', letterSpacing: '-0.03em' }}
            >
              {t("hero.title.line1")}
              <br className="hidden sm:block" />
              {" "}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 via-amber-400 to-yellow-500">
                {t("hero.title.line2")}
              </span>
              <br className="hidden sm:block" />
              {" "}
              {t("hero.title.line3")}
            </h1>
            <p className="text-lg text-white/70 max-w-3xl mx-auto leading-relaxed">
              {t("hero.subtitle")}
            </p>
          </motion.div>

          {/* Upload Section */}
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-10 mb-8">
            {/* Left Column - Upload */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <div className="glass-card rounded-2xl p-8">
                <FileUploader
                  file={file}
                  isUploading={isUploading}
                  isDragOver={isDragOver}
                  uploadProgress={uploadProgress}
                  domains={domains}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                  onFileSelect={handleFileSelect}
                  onRemoveFile={removeFile}
                />

                <SearchConfig
                  countryCode={countryCode}
                  adFormat={adFormat}
                  onCountryChange={setCountryCode}
                  onFormatChange={setAdFormat}
                />

                {/* Payment Buttons */}
                {fileId && (
                  <CheckoutButtons
                    selectedTier={selectedTier}
                    isProcessing={isProcessing}
                    onCheckout={onCheckout}
                    domainCount={domains.length}
                  />
                )}

                {/* Micro-copy */}
                <p className="text-xs text-center text-white/40 mt-4">
                  {t("hero.microcopy")}
                </p>
              </div>
            </motion.div>

            {/* Right Column - Preview */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
            >
              <DomainPreview domains={domains} />
            </motion.div>
          </div>
        </>
      )}
    </section>
  );
};

export default HeroSection;
